import type { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';

import { LogoutButton } from '../../features/auth/LogoutButton';

import type { NavBadge, NavGroup, NavItem } from './nav-types';
import { NavIcon } from './NavIcon';
import s from './Sidebar.module.css';
import { SidebarAccount } from './SidebarAccount';

interface SidebarProps {
  brand: string;
  workspace: string;
  homeHref: string;
  /** Where this workspace keeps the account screen; the foot block links to it. */
  profileHref: string;
  groups: readonly NavGroup[];
  /** Rail mode: icons only, labels carried by `title` + `aria-label`. */
  collapsed: boolean;
  /** Absent inside the mobile drawer, where the header shows a close button. */
  onToggleCollapsed?: () => void;
  /** The mobile drawer's dismiss control, rendered in place of the rail toggle. */
  closeControl?: ReactNode;
  /**
   * Live counts, resolved by the shell and drawn on the rows that asked for
   * them. A missing or zero entry draws nothing at all — an empty badge is a
   * worse answer than no badge.
   */
  badges?: Readonly<Partial<Record<NavBadge, number>>>;
  /**
   * Cross-fade the route on navigation. The mobile drawer passes false: its
   * own dismissal is the dominant motion, and a fade under a closing modal
   * reads as double motion.
   */
  viewTransition?: boolean;
}

function itemTitle(item: NavItem): string {
  return item.disabled === true ? `${item.label} — coming soon` : item.label;
}

interface CountProps {
  count: number;
}

/**
 * The unread count, sitting where the row's own trailing slot is.
 *
 * The number is `aria-hidden` and the meaning is spelled out beside it, so the
 * row's accessible name reads "Notifications 3 unread" rather than
 * "Notifications 3". The spoken half is a SIBLING of the badge, not a child:
 * the collapsed rail shrinks the badge to a 6px dot with `font-size: 0`, which
 * would take any text inside it down too.
 */
function NavCount({ count }: CountProps): JSX.Element {
  return (
    <>
      <span className={s.count} aria-hidden="true">
        {count > 99 ? '99+' : count}
      </span>
      <span className={s.srOnly}>{count} unread</span>
    </>
  );
}

export function Sidebar({
  brand,
  workspace,
  homeHref,
  profileHref,
  groups,
  collapsed,
  onToggleCollapsed,
  closeControl,
  badges,
  viewTransition = true,
}: SidebarProps): JSX.Element {
  return (
    <div className={collapsed ? `${s.root} ${s.rail}` : s.root}>
      <div className={s.head}>
        <Link to={homeHref} className={s.brand} title={`${brand} — ${workspace}`}>
          <span className={s.mark} aria-hidden="true">
            i
          </span>
          <span className={s.brandText}>
            <span className={s.brandName}>{brand}</span>
            <span className={s.brandSub}>{workspace}</span>
          </span>
          {/* The visual label is split across two spans and the mark is
              decorative, so spell the name out once for assistive tech. */}
          <span className={s.srOnly}>
            {brand} — {workspace}
          </span>
        </Link>
        {closeControl}
        {onToggleCollapsed !== undefined ? (
          <button
            type="button"
            className={s.railToggle}
            onClick={onToggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-pressed={collapsed}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M2.5 3.25h11a1 1 0 0 1 1 1v7.5a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1v-7.5a1 1 0 0 1 1-1Z" />
              <path d="M6.25 3.25v9.5" />
            </svg>
          </button>
        ) : null}
      </div>

      <nav className={s.nav} aria-label="Primary">
        {groups.map((group) => (
          <div key={group.label} className={s.group}>
            <p className={s.groupLabel}>{group.label}</p>
            <ul className={s.list}>
              {group.items.map((item) => {
                const count = item.badge === undefined ? 0 : (badges?.[item.badge] ?? 0);
                return (
                  <li key={item.to} className={s.item}>
                    {item.disabled === true ? (
                      <span aria-disabled="true" className={s.rowDisabled} title={itemTitle(item)}>
                        <NavIcon name={item.icon} className={s.icon} />
                        <span className={s.label}>{item.label}</span>
                        <span className={s.soon}>Soon</span>
                      </span>
                    ) : (
                      <NavLink
                        to={item.to}
                        end={item.matchSubRoutes !== true}
                        viewTransition={viewTransition}
                        title={itemTitle(item)}
                        className={({ isActive }) =>
                          isActive ? `${s.row} ${s.rowActive}` : (s.row ?? '')
                        }
                      >
                        <NavIcon name={item.icon} className={s.icon} />
                        <span className={s.label}>{item.label}</span>
                        {count > 0 ? <NavCount count={count} /> : null}
                      </NavLink>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className={s.foot}>
        <SidebarAccount compact={collapsed} workspace={workspace} profileHref={profileHref} />
        <LogoutButton compact={collapsed} />
      </div>
    </div>
  );
}
